import { useGet } from './templateHooks';
import { Tag, TagWithTotalCounts } from 'src/@types/tag';
import { CombinedModelType, ModelRunType, ModelType } from 'src/@types/models';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useAxios } from './useFetch';
import { FAST_API_URL } from 'src/config';

export const useModels = (config = {}) =>
  useGet<ModelType[]>({
    queryKey: ['models'],
    endpoint: '/v1/models',
    config: config,
    url: FAST_API_URL,
  });

export const useCombinedModels = (config = {}) =>
  useGet<CombinedModelType[]>({
    queryKey: ['models', 'combined'],
    endpoint: '/v1/models/combined',
    config: config,
    url: FAST_API_URL,
  });

export const useModelableTags = (config = {}) =>
  useGet<TagWithTotalCounts[]>({
    queryKey: ['modelable-tags'],
    endpoint: '/v1/modelable-tags',
    config: config,
    url: FAST_API_URL,
  });

export const useModelTags = (config = {}) =>
  useGet<Tag[]>({
    queryKey: ['tag-models'],
    endpoint: '/v1/tag-models',
    config: config,
    url: FAST_API_URL,
  });

export const useStartModelRun = () => {
  const queryClient = useQueryClient();

  const mutationFn = useAxios<undefined, ModelRunType>({
    endpoint: '/v1/models/run',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] });
      queryClient.invalidateQueries({ queryKey: ['tag-models'] });
      queryClient.invalidateQueries({ queryKey: ['models', 'combined'] });
    },
  });
};

export const useReRunModel = () => {
  const queryClient = useQueryClient();

  const mutationFn = useAxios<undefined, ModelRunType>({
    endpoint: '/v1/models/run',
    method: 'post',
    url: FAST_API_URL,
  });
  const mutation = useMutation({ mutationFn });

  const reRunModel = async (modelId: string) => {
    await mutation.mutateAsync({ resourceId: modelId });
    queryClient.invalidateQueries({ queryKey: ['models'] });
    queryClient.invalidateQueries({ queryKey: ['tag-models'] });
    queryClient.invalidateQueries({ queryKey: ['models', 'combined'] });
  };

  return { reRunModel, ...mutation };
};

export const useRunPendingModels = () => {
  const mutationFn = useAxios<undefined, undefined>({
    endpoint: '/v1/models/import-pending',
    method: 'post',
    url: FAST_API_URL,
  });
  return useMutation({ mutationFn });
};

export const useDeleteModels = () => {
  const queryClient = useQueryClient();

  const mutationFn = useAxios<undefined, { ids: string[] }>({
    endpoint: '/v1/models/combined',
    method: 'delete',
    url: FAST_API_URL,
  });
  return useMutation({
    mutationFn,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['models'] });
      queryClient.invalidateQueries({ queryKey: ['tag-models'] });
      queryClient.invalidateQueries({ queryKey: ['filterCategories'] });
      queryClient.invalidateQueries({ queryKey: ['models', 'combined'] });
    },
  });
};
